import React, { useState, useEffect } from 'react';
import { Modal, Switch, ActionIcon, Text, Stack, Group, TextInput, Paper, Divider, Loader, Table } from '@mantine/core';
import { IconFilter, IconPlus, IconShield, IconTrash, IconX, IconLoader } from '@tabler/icons';
import { useIgnoreAccountIds, useUpdateIgnoreAccountIds } from '../../../../hooks/useSettings';

interface IgnoreAccountIdsModalProps {
  opened: boolean;
  onClose: () => void;
}

export const IgnoreAccountIdsModal: React.FC<IgnoreAccountIdsModalProps> = ({ opened, onClose }) => {
  // Ignore Account IDs state
  const { data: ignoreAccountIdsConfig, isLoading: isLoadingConfig } = useIgnoreAccountIds();
  const updateIgnoreAccountIds = useUpdateIgnoreAccountIds();
  
  const [isEnabled, setIsEnabled] = useState(false);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [newAccountId, setNewAccountId] = useState('');
  const [inputError, setInputError] = useState('');
  const [loadingStates, setLoadingStates] = useState<{
    addNew: boolean;
    toggle: boolean;
    deleteIds: Record<string, boolean>;
  }>({
    addNew: false,
    toggle: false,
    deleteIds: {},
  });

  // Initialize state when config is loaded
  useEffect(() => {
    if (ignoreAccountIdsConfig) {
      setIsEnabled(ignoreAccountIdsConfig.isEnabled);
      setAccountIds([...ignoreAccountIdsConfig.accountIds]);
    }
  }, [ignoreAccountIdsConfig]);

  const saveChanges = async (enabled: boolean, ids: string[]) => {
    try {
      await updateIgnoreAccountIds.mutateAsync({
        isEnabled: enabled,
        accountIds: ids
      });
    } catch (error) {
      console.error("Failed to update ignore account IDs:", error);
    }
  };

  const handleAddAccountId = async () => {
    // Validate the input - no spaces allowed
    if (newAccountId.includes(' ')) {
      setInputError('Account ID cannot contain spaces');
      return;
    }
    
    if (newAccountId.trim() && !accountIds.includes(newAccountId.trim())) {
      try {
        setLoadingStates(prev => ({ ...prev, addNew: true }));
        const updatedIds = [...accountIds, newAccountId.trim()];
        await saveChanges(isEnabled, updatedIds);
        setAccountIds(updatedIds);
        setNewAccountId('');
        setInputError('');
      } finally {
        setLoadingStates(prev => ({ ...prev, addNew: false }));
      }
    }
  };

  const handleRemoveAccountId = async (id: string) => {
    try {
      setLoadingStates(prev => ({
        ...prev,
        deleteIds: { ...prev.deleteIds, [id]: true }
      }));
      const updatedIds = accountIds.filter(accId => accId !== id);
      await saveChanges(isEnabled, updatedIds);
      setAccountIds(updatedIds);
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        deleteIds: { ...prev.deleteIds, [id]: false }
      }));
    }
  };

  const handleToggleEnabled = async (checked: boolean) => {
    try {
      setLoadingStates(prev => ({ ...prev, toggle: true }));
      setIsEnabled(checked);
      await saveChanges(checked, accountIds);
    } finally {
      setLoadingStates(prev => ({ ...prev, toggle: false }));
    }
  };

  const handleNewAccountIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewAccountId(value);
    
    // Clear error if input is empty or valid
    if (!value || !value.includes(' ')) {
      setInputError('');
    } else {
      setInputError('Account ID cannot contain spaces');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      padding={0}
      withCloseButton={false}
      closeOnClickOutside
      closeOnEscape
      centered
    >
      <Group p="xs" position='apart'>
        <Group spacing={8}>
          <IconFilter size={18} stroke={1.5}/>
          <Text fw={500}>Global Filters</Text>
        </Group>

        
        <ActionIcon onClick={onClose}>
          <IconX size={16} />
        </ActionIcon>
      </Group>
      <Divider />
      <Stack spacing="md" p="md">
        <Group position="apart" align="self-start">
          <Text>Enable account ID filtering</Text>
          <Switch
            checked={isEnabled}
            onChange={(event) => handleToggleEnabled(event.currentTarget.checked)}
            disabled={loadingStates.toggle}
          />
        </Group>
        
        <Text size="sm" color="dimmed">
          Accounts with these IDs will be ignored in all integrations.
        </Text>
        
        <Group spacing={0} align="self-start">
          <TextInput
            placeholder="Enter account ID"
            value={newAccountId}
            onChange={handleNewAccountIdChange}
            style={{ 
              flex: 1,
            }}
            error={inputError}
            styles={{
              input: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !inputError) {
                handleAddAccountId();
              }
            }}
            disabled={loadingStates.addNew}
          />
          <ActionIcon 
            color='indigo'
            variant="filled" 
            onClick={handleAddAccountId}
            disabled={!newAccountId.trim() || loadingStates.addNew || !!inputError}
            sx={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              '&:disabled': {
                border: '0.0625rem solid #ced4da',
                borderLeft: 'none'
              }
            }}
            w={36}
            h={36}
          >
            {loadingStates.addNew ? <Loader size={16}/> : <IconPlus size={16} />}
          </ActionIcon>
        </Group>
        
        <Paper withBorder p={0}>
          {accountIds.length === 0 ? (
            <Text color="dimmed" size="sm" align="center" py="sm">No account IDs added</Text>
          ) : (
            <Table>
              <thead style={{ backgroundColor: 'rgba(92, 92, 235, 0.05)' }}>
                <tr>
                  <th>Account ID</th>
                  <th style={{ width: 70, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {accountIds.map((id) => (
                  <tr key={id}>
                    <td><Text size="sm">{id}</Text></td>
                    <td style={{ justifyItems: 'right' }}>
                      <ActionIcon 
                        color="red" 
                        onClick={() => handleRemoveAccountId(id)}
                        disabled={loadingStates.deleteIds[id]}
                      >
                        {loadingStates.deleteIds[id] ? <Loader size={14}/> : <IconTrash size={16} />}
                      </ActionIcon>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Modal>
  );
}; 